import { Stack, Typography } from "@mui/material";
import type { OverridableStringUnion } from "@mui/types";
import type { TypographyVariant } from "@mui/material/styles/createTypography";
import type { TypographyPropsVariantOverrides } from "@mui/material/Typography";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/AppliedFilters";
import { SearchFilters } from "~/Search/components/Filters/SearchFilters";
import { selectVisibleFilters } from "~/Search/redux/filterConfigSelectors";
import { useSelector } from "react-redux";
import { selectProductResultAmount, selectSearchDisplayMode } from "~/Search/redux/searchSelectors";
import { isComingFromSeriesTreeClick } from "~/Search/redux/seriesTreeSelectors";
import { Filter } from "~/Search/filterConfigModels";
import { SearchDisplayMode } from "~/Search/models";
import { SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import type { RootState } from "~/redux/store";
import { useSelectAppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";

interface Props {
  titleFontVariant?: OverridableStringUnion<TypographyVariant | "inherit", TypographyPropsVariantOverrides>;
  hideTechFilterButtons?: boolean;
}

export const ReduxFilterSection = memo(function ReduxFilterSection({
  titleFontVariant = "headlineSmall",
  hideTechFilterButtons,
}: Props) {
  const { t } = useTranslation();
  const isTabularDisplay = useSelector(selectSearchDisplayMode) === SearchDisplayMode.TABULAR;
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const isPlp = useSearchPageType() === SearchPageType.PLP;

  const hideNonCategoryFilters = isTabularDisplay && !isProductFamilySelected && !isPlp;

  let filterConfig = useSelector(selectVisibleFilters);

  const productAmount = useSelector(selectProductResultAmount);
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);

  const appliedFilters = useSelectAppliedFilters();

  if (isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  if (!productAmount && appliedFilters.length === 0 && filterConfig.length === 0) {
    return null;
  }

  return (
    <Stack spacing={{ phone: 1, "desktop-s": 2 }}>
      <Typography variant={titleFontVariant} sx={{
        fontWeight: "bold"
      }}>
        {t("page.search.filters.title")}
      </Typography>
      <AppliedFilters appliedFilters={appliedFilters} hideClearAll={hideTechFilterButtons} />
      <SearchFilters
        filterConfig={filterConfig}
        hideTechFilterButtons={hideTechFilterButtons}
        hideNonCategoryFilters={hideNonCategoryFilters}
      />
    </Stack>
  );
});

const selectIsProductFamilySelected = (state: RootState) => state.srp.selectedProductGroups.length > 0;
